import type { LexiconEntry } from './lexicon-schema.js'

/**
 * Secondary-Dominant Suggestion Chip v1 lexicon — one parameterized concept
 * entry. The `{{target}}` placeholder is interpolated at the use site with the
 * chip's resolution-target roman numeral (e.g. `vi`, `V`), so a single entry
 * covers all four offered secondary dominants (V/ii, V/IV, V/V, V/vi) instead
 * of four near-identical entries (brief §5).
 *
 * Honesty constraints (brief §4): the copy names the chord as borrowed /
 * chromatic and states where it resolves — never "in key", never a riddle with
 * no stated target. Wording authored for Bowie review (brief §5, §8).
 *
 * Distinct from the Reharm Lab's `subst-secondary-dominant` entry, which frames
 * the technique as an *insertion* move in the reharmonization tool. This entry
 * frames the same harmony as a *suggestion-explainer* concept (why this chip).
 *
 * Spec: docs/superpowers/specs/2026-06-06-secondary-dominant-chip-brief.md
 */
export const SECONDARY_DOMINANT_LEXICON_ENTRIES: LexiconEntry[] = [
  {
    id: 'secondary-dominant',
    category: 'substitution',
    displayName: 'Secondary dominant',
    displayNameShort: 'V7/X',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'A borrowed chord that briefly makes {{target}} feel like home, then resolves to it.',
      intermediate: 'Secondary dominant — a chromatic dom7 that borrows V\'s pull to tonicize {{target}}, then resolves to it.',
      advanced:
        'Secondary dominant (V7/{{target}}) — chromatic dom7 a fifth above {{target}}; its tritone and raised leading-tone tonicize {{target}} by borrowed V→I motion, resolving down a fifth.',
    },
    tagLabel: { beginner: 'leads to {{target}}', intermediate: 'tonicizes {{target}}', advanced: 'V7/{{target}}' },
  },
]
